import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session } from '@supabase/auth-js';
import { brandingFollowsSession, getHostAudience, getSupabase, useBranding } from '@l2u/web-shared';
import { setCompanyAccessToken } from '@l2u/web-shared';
import { browserDraftStorage, clearAllDrafts } from '../answerDraft.ts';

type CompanyAuthValue = {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
};

const CompanyAuthContext = createContext<CompanyAuthValue>({
  session: null,
  loading: true,
  signOut: async () => {},
});

export function CompanyAuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { reload: reloadBranding } = useBranding();

  // BrandingProvider mounts (and does its first, token-less fetch) above this
  // provider. On a host that names no tenant that fetch can only return the
  // platform brand, so once the signed-in USER changes — session restore,
  // company or driver magic-link sign-in (both flow through this shared
  // supabase client), sign-out — re-fetch so the portal picks up the tenant's
  // branding (the API resolves the tenant from the bearer token).
  //
  // Keyed on the user, not the token: a token refresh cannot change the tenant
  // the bearer resolves to, and re-fetching on each one was a branding round
  // trip every hour. And not at all on a tenant's own audience host, where the
  // token-less fetch already resolved this tenant from the hostname — see
  // brandingFollowsSession. Skip the first run: that fetch already happened in
  // BrandingProvider's own mount effect.
  const brandingReloadPrimed = useRef(false);
  const sessionUserId = session?.user.id ?? null;
  useEffect(() => {
    if (!brandingReloadPrimed.current) {
      brandingReloadPrimed.current = true;
      return;
    }
    if (!brandingFollowsSession(hostAudience())) return;
    void reloadBranding();
  }, [sessionUserId, reloadBranding]);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | null = null;
    const supabase = (() => {
      try {
        return getSupabase();
      } catch {
        return null;
      }
    })();

    if (!supabase) {
      setLoading(false);
      return () => {};
    }

    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
      setCompanyAccessToken(data.session?.access_token ?? null);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!mounted) return;
      setSession(next ?? null);
      setCompanyAccessToken(next?.access_token ?? null);
    });
    unsubscribe = () => subscription.subscription.unsubscribe();

    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      const supabase = getSupabase();
      await supabase.auth.signOut();
    } finally {
      setSession(null);
      setCompanyAccessToken(null);
      // Unsent questionnaire answers must not outlive the session that staged
      // them. The draft key already carries the account id, so nobody else can
      // READ these — but a shared depot tablet is a realistic deployment for a
      // driver portal, and compliance answers left in a stranger's browser are
      // not something to keep on the chance the same person returns.
      //
      // Here rather than in the shells' confirmSignOut: this is the single
      // choke point every sign-out goes through, including the involuntary one
      // both shells fire on a 401.
      clearAllDrafts(browserDraftStorage());
    }
  }, []);

  const value = useMemo<CompanyAuthValue>(
    () => ({ session, loading, signOut }),
    [session, loading, signOut],
  );

  return <CompanyAuthContext.Provider value={value}>{children}</CompanyAuthContext.Provider>;
}

// The boot gate resolves /api/config before this provider mounts, so this
// normally answers; if it ever throws, fall back to re-fetching — the old,
// always-correct behaviour.
function hostAudience(): string | null {
  try {
    return getHostAudience();
  } catch {
    return null;
  }
}

export function useCompanyAuth(): CompanyAuthValue {
  return useContext(CompanyAuthContext);
}
